/// <reference types="@angular/localize" />

import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { LicenseServiceGrid, RoleTemplateServiceGrid } from '@myapp/features/admin/services';
import { ConfirmationDialogHtmlPaneService, ConfirmationDialogService, UiWindowService, UserService } from '@myapp/modules/fwcore/services';
import { NgbAlertModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OKTA_CONFIG, OktaAuthGuard, OktaAuthModule, OktaCallbackComponent } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { NotificationService } from '@progress/kendo-angular-notification';
import { AppComponent } from './app/app.component';
import { FwcoreModule } from './app/modules/fwcore/fwcore.module';
import { ApplicationInsightsErrorHandler } from './app/modules/fwcore/handlers';
import { HomePageComponent, LoginComponent, PageNoLicenseComponent, PageNotAuthorizedComponent, PageNotFoundComponent } from './app/modules/fwcore/pages';
import { environment } from './environments/environment';
import { ApiKeyInterceptor } from './interceptors/api-key.interceptor';
import { ErrorIntercept } from './interceptors/error.interceptor';
import { InterceptorDateHttp } from './interceptors/interceptorDateHttp';
import { LoaderInterceptor } from './interceptors/interceptorLoader';

import { PageQuestionSearchComponent } from './app/features/question/pages/page-question-search/page-question-search.component';
import { PageQuestionComponent } from './app/features/question/pages/page-question/page-question.component';
import { PageViewQuestionsComponent } from './app/features/question/pages/page-view-questions/page-view-questions.component';
import { PageSettingComponent } from './app/features/setting/pages/page-setting/page-setting.component';
import { PageSurveySectionLanguageSettingsComponent } from './app/features/survey/pages/page-survey-section-language-settings/page-survey-section-language-settings.component';
import { PageSurveySectionComponent } from './app/features/survey/pages/page-survey-section/page-survey-section.component';
import { initSettingsTabs } from './app/modules/fwcore/components/user-settings/user-settings.func';
//import { SurveySectionLanguageSettingsComponent } from './app/features/setting/components/surveysectionlanguagesettings/surveysectionlanguagesettings-component';
import { CreateSettingsComponent } from './app/features/setting/components/createsettings/createsettings.component';
import { PreviewQuestionComponent } from './app/features/question/pages/page-preview-question/page-preview-question.component';
import { PageQuestionLanguageSettingsComponent } from './app/features/question/pages/page-question-language-settings/page-question-language-settings.component';
import { PageCreateDescInputComponent } from './app/features/question/pages/page-create-desc-input/page-create-desc-input.component';
import { PageCreateInputTypeComponent } from './app/features/question/pages/page-create-input-type/page-create-input-type.component';
import { PageQuestionMatrixComponent } from './app/features/question/pages/page-questionmatrix/page-questionmatrix.component';
import { PagePicklistQuestionComponent } from './app/features/question/pages/page-picklist-question/page-picklist-question.component';
import { PageMultipleChoiceComponent } from './app/features/question/pages/page-multiple-choice/page-multiple-choice.component';
import { PageMultipleResponseComponent } from './app/features/question/pages/page-multiple-response/page-multiple-response.component';
import { PageSliderQuestionComponent } from './app/features/question/pages/page-slider-question/page-slider-question.component';
import { PageConstantSumComponent } from './app/features/question/pages/page-constant-sum/page-constant-sum.component';
import { PageCreateDatetimeTypeComponent } from './app/features/question/pages/page-create-datetime-type/page-create-datetime-type.component';
import { PageRankingsortTypeComponent } from './app/features/question/pages/page-rankingsort-type/page-rankingsort-type.component';
import { PageSurveyComponent } from './app/features/survey/pages/page-survey/page-survey.component';
import { CompactLayoutComponent } from './app/features/layouts/compact-layout/compact-layout.component';
import { PageSurveyPreviewComponent } from './app/features/survey/pages/page-survey-preview/page-survey-preview.component';
import { PageUserComponent } from './app/features/user/pages/page-user/page-user.component';
import { PageCompanyComponent } from './app/features/company/pages/page-company/page-company.component';
import { PageSurveyServiceLineComponent } from './app/features/serviceline/pages/page-survey-service-line/page-survey-service-line.component';
import { ManageServiceLineComponent } from './app/features/serviceline/components/manageserviceline/manage-service-line.component';

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}

// Get okta settings before bootstrapping
fetch('/assets/configuration/appconfig.json')
  .then(response => response.json())
  .then(config => {

    const providersConfig = [
      { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
      { provide: 'CONFIG_SETTINGS', useValue: config }
    ];

    if (environment.production) {
      enableProdMode();
    }

    bootstrapApplication(AppComponent
      , {
        providers: [
          importProvidersFrom(
            BrowserModule
            , OktaAuthModule
            , NgbModule
            , NgbAlertModule
            , FwcoreModule
            , NgIdleKeepaliveModule.forRoot()
          )
          , providersConfig
          , {
            provide: OKTA_CONFIG, useFactory: (config: 'CONFIG_SETTINGS') => {
              const oktaSettings = config[<any>'okta'];
              const oktaConfig = {
                issuer: oktaSettings[<any>'issuer']
                , redirectUri: oktaSettings[<any>'redirectUri']
                , postLogoutRedirectUri: oktaSettings[<any>'postLogoutRedirectUri']
                , clientId: oktaSettings[<any>'clientId']
                , scopes: Array.from(oktaSettings[<any>'scopes'])
                , pkce: JSON.parse(oktaSettings[<any>'pkce'])

                // Token management settings
                , tokenManager: {
                  storage: 'localStorage', // or 'sessionStorage', 'memory'
                  autoRenew: true,
                  secure: true,
                  expireEarlySeconds: 300 // Renew 5 minutes before expiration
                },

                // Services configuration
                services: {
                  autoRenew: true,
                  autoRemove: true
                }
              };
              const oktaAuth = new OktaAuth(oktaConfig);
              return { oktaAuth };
            }
            , deps: ['CONFIG_SETTINGS']
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: ApiKeyInterceptor
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: InterceptorDateHttp
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: LoaderInterceptor
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: ErrorIntercept
            , multi: true
          }
          , {
            provide: APP_INITIALIZER
            , useFactory: initSettingsTabs
            , deps: [UserService]
            , multi: true
          }
          , {
            provide: ErrorHandler
            , useClass: ApplicationInsightsErrorHandler
          }
          , provideHttpClient(withInterceptorsFromDi())
          , provideAnimations()
          , UiWindowService
          , NotificationService
          , ConfirmationDialogService
          , ConfirmationDialogHtmlPaneService
          , LicenseServiceGrid
          , RoleTemplateServiceGrid
          , provideRouter([
            { path: '', redirectTo: 'login', pathMatch: 'full' }
            , { path: 'home', component: HomePageComponent, canActivate: [OktaAuthGuard] }
            , { path: 'login', component: LoginComponent }
            , { path: 'login/callback', component: OktaCallbackComponent }
            , { path: 'question', component: PageQuestionComponent, canActivate: [OktaAuthGuard] }
            , { path: 'setting', component: PageSettingComponent, canActivate: [OktaAuthGuard] }
            , { path: 'questionSearch', component: PageQuestionSearchComponent, canActivate: [OktaAuthGuard] }
            , { path: 'viewquestions', component: PageViewQuestionsComponent, canActivate: [OktaAuthGuard] }
            , { path: 'section', component: PageSurveySectionComponent, canActivate: [OktaAuthGuard] }
            , { path: 'surveysectionlanguagesettings', component: PageSurveySectionLanguageSettingsComponent, canActivate: [OktaAuthGuard] }
            , { path: 'createsettings', component: CreateSettingsComponent, canActivate: [OktaAuthGuard] }
            , { path: 'previewquestion', component: PreviewQuestionComponent, canActivate: [OktaAuthGuard] }
            , { path: 'questionlanguagesettings', component: PageQuestionLanguageSettingsComponent, canActivate: [OktaAuthGuard] }
            , { path: 'descriptionType', component: PageCreateDescInputComponent, canActivate: [OktaAuthGuard] }
            , { path: 'inputType', component: PageCreateInputTypeComponent, canActivate: [OktaAuthGuard] }
            , { path: 'questionmatrix', component: PageQuestionMatrixComponent, canActivate: [OktaAuthGuard] }
            , { path: 'picklistquestion', component: PagePicklistQuestionComponent, canActivate: [OktaAuthGuard] }
            , { path: 'multiplechoice', component: PageMultipleChoiceComponent, canActivate: [OktaAuthGuard] }
            , { path: 'multipleresponse', component: PageMultipleResponseComponent, canActivate: [OktaAuthGuard] }
            , { path: 'slider', component: PageSliderQuestionComponent, canActivate: [OktaAuthGuard] }
            , { path: 'constantsum', component: PageConstantSumComponent, canActivate: [OktaAuthGuard] }
            , { path: 'datetimeType', component: PageCreateDatetimeTypeComponent, canActivate: [OktaAuthGuard] }
            , { path: 'rankingsort', component: PageRankingsortTypeComponent, canActivate: [OktaAuthGuard] }
            , { path: 'survey', component: PageSurveyComponent, pathMatch: 'full', canActivate: [OktaAuthGuard] }
            , { path: 'survey-preview', component: PageSurveyPreviewComponent, pathMatch: 'full', canActivate: [OktaAuthGuard] }

            //, {
            //  path: '',
            //  canActivate: [OktaAuthGuard],
            //  data: { /*onAuthRequired*/ },
            //  component: CompactLayoutComponent,
            //  children: [
            //    {
            //      path: 'survey-preview', component: PageSurveyPreviewComponent, pathMatch: 'full', canActivate: [OktaAuthGuard],
            //    }]
            //}

            
           
            , { path: 'user', component: PageUserComponent, canActivate: [OktaAuthGuard] }
            , { path: 'company', component: PageCompanyComponent, canActivate: [OktaAuthGuard] }
            , { path: 'serviceline', component: PageSurveyServiceLineComponent, canActivate: [OktaAuthGuard] } 
            , { path: 'manageserviceline', component: ManageServiceLineComponent, canActivate: [OktaAuthGuard] }


            , {
              path: 'admin'
              , loadChildren: () => import('./app/features/admin/admin.routes')
                .then((r => r.ADMIN_ROUTES))
            }
            //, {
            //  path: 'deals'
            //  , loadChildren: () => import('./app/features/prob/prob-deals.routes')
            //    .then((r => r.PROB_DEALS_ROUTES))
            //}
            , { path: 'error/404', component: PageNotFoundComponent }
            , { path: 'error/not-authorized', component: PageNotAuthorizedComponent }
            , { path: 'not-authorized', redirectTo: 'error/not-authorized' }
            , { path: 'no-license', component: PageNoLicenseComponent }
            , { path: '**', redirectTo: 'error/404' }
          ])
        ]
      })
      .catch(err => console.log(err));

  })
